/* eslint-disable @typescript-eslint/indent */
import React from 'react';
import {
  Card,
  CardHeader,
  CardContent
} from './style';
import styled, { css } from 'styled-components';
import { formatNumber } from '~/utils/helpers';

interface EmissionCardProps {
  title: string;
  value: number;
  unit: string;
  description?: string;
  variant?: 'default' | 'warning' | 'danger' | 'cows' | 'youngCattle' | 'grassSilage' | 'maizeSilage';
  icon?: React.ReactNode;
  isActive?: boolean;
  onClick?: () => void;
  additionalDetail?: {
    value: number;
    unit: string;
    label: string;
  };
}

const Badge = styled.span<{ $variant: EmissionCardProps['variant'] }>`
  display: inline-block;
  padding: 0.25rem 0.5rem;
  border-radius: 1rem;
  font-size: 0.75rem;
  font-weight: 500;
  white-space: nowrap;

  ${(props) => {
    switch (props.$variant) {
      case 'cows':
        return css`
          background-color: #4A90E2;
          color: #fff;
        `;
      case 'youngCattle':
        return css`
          background-color: #FF9800;
          color: #fff;
        `;
      case 'grassSilage':
        return css`
          background-color: #4CAF50;
          color: #fff;
        `;
      case 'maizeSilage':
        return css`
          background-color: #FFC107;
          color: #fff;
        `;
      default:
        return css`
          background-color: #2185d0;
          color: #fff;
        `;
    }
  }}
`;

const VariantWrapper = styled(Card) <{ $variant: EmissionCardProps['variant']; $isActive?: boolean; $onClick?: () => void; $hasAdditionalDetail?: boolean }>`
  transition: all 0.3s ease-in-out;
  transform: ${props => props.$isActive ? 'scale(1.05)' : 'scale(1)'};
  z-index: ${props => props.$isActive ? '10' : '1'};
  cursor: ${props => props.$onClick ? 'pointer' : 'default'};
  position: relative;
  min-height: ${props => props.$hasAdditionalDetail ? '160px' : '140px'};
  
  ${props => props.$isActive && css`
    &::before {
      content: '';
      position: absolute;
      top: -3px;
      left: -3px;
      right: -3px;
      bottom: -3px;
      border-radius: inherit;
      background: linear-gradient(45deg, 
        ${props.$variant === 'cows' ? '#4A90E2' :
      props.$variant === 'youngCattle' ? '#FF9800' :
        props.$variant === 'grassSilage' ? '#4CAF50' :
          props.$variant === 'maizeSilage' ? '#FFC107' : '#4A90E2'}, 
        transparent, 
        ${props.$variant === 'cows' ? '#4A90E2' :
      props.$variant === 'youngCattle' ? '#FF9800' :
        props.$variant === 'grassSilage' ? '#4CAF50' :
          props.$variant === 'maizeSilage' ? '#FFC107' : '#4A90E2'}
      );
      z-index: -1;
    }
  `}

  ${(props) => {
    const isActive = props.$isActive;
    switch (props.$variant) {
      case 'warning':
        return css`
          border: 1px solid #ffa500;
          background: linear-gradient(to bottom right, #fff7e6, #ffe0b3);
        `;
      case 'danger':
        return css`
          border: 1px solid #ff4d4f;
          background: linear-gradient(to bottom right, #ffe6e6, #ffcccc);
        `;
      case 'cows':
        return css`
          border: ${isActive ? '3px solid #4A90E2' : '1px solid #4A90E2'};
          background: ${isActive
            ? 'linear-gradient(135deg, #E3F2FD 0%, #BBDEFB 50%, #90CAF9 100%)'
            : 'linear-gradient(to bottom right, #E3F2FD, #BBDEFB)'};
          box-shadow: ${isActive
            ? '0 12px 35px rgba(74, 144, 226, 0.4), 0 0 20px rgba(74, 144, 226, 0.2)'
            : '0 1px 2px rgba(0, 0, 0, 0.05)'};
          
          &:hover {
            box-shadow: ${isActive
            ? '0 12px 35px rgba(74, 144, 226, 0.4), 0 0 20px rgba(74, 144, 226, 0.2)'
            : '0 4px 12px rgba(74, 144, 226, 0.2)'};
          }
        `;
      case 'youngCattle':
        return css`
          border: ${isActive ? '3px solid #FF9800' : '1px solid #FF9800'};
          background: ${isActive
            ? 'linear-gradient(135deg, #FFF3E0 0%, #FFE0B2 50%, #FFCC02 100%)'
            : 'linear-gradient(to bottom right, #FFF3E0, #FFE0B2)'};
          box-shadow: ${isActive ? '0 12px 35px rgba(255, 152, 0, 0.4), 0 0 20px rgba(255, 152, 0, 0.2)'
            : '0 1px 2px rgba(0, 0, 0, 0.05)'};
          
          &:hover {
            box-shadow: ${isActive ? '0 12px 35px rgba(255, 152, 0, 0.4), 0 0 20px rgba(255, 152, 0, 0.2)'
            : '0 4px 12px rgba(255, 152, 0, 0.2)'};
          }
        `;
      case 'grassSilage':
        return css`
          border: ${isActive ? '3px solid #4CAF50' : '1px solid #4CAF50'};
          background: ${isActive ? 'linear-gradient(135deg, #E8F5E8 0%, #C8E6C9 50%, #A5D6A7 100%)'
            : 'linear-gradient(to bottom right, #E8F5E8, #C8E6C9)'};
          box-shadow: ${isActive ? '0 12px 35px rgba(76, 175, 80, 0.4), 0 0 20px rgba(76, 175, 80, 0.2)'
            : '0 1px 2px rgba(0, 0, 0, 0.05)'};
          
          &:hover {
            box-shadow: ${isActive
            ? '0 12px 35px rgba(76, 175, 80, 0.4), 0 0 20px rgba(76, 175, 80, 0.2)'
            : '0 4px 12px rgba(76, 175, 80, 0.2)'};
          }
        `;
      case 'maizeSilage':
        return css`
          border: ${isActive ? '3px solid #FFC107' : '1px solid #FFC107'};
          background: ${isActive
            ? 'linear-gradient(135deg, #FFFDE7 0%, #FFF9C4 50%, #FFF176 100%)'
            : 'linear-gradient(to bottom right, #FFFDE7, #FFF9C4)'};
          box-shadow: ${isActive
            ? '0 12px 35px rgba(255, 193, 7, 0.4), 0 0 20px rgba(255, 193, 7, 0.2)'
            : '0 1px 2px rgba(0, 0, 0, 0.05)'};
          
          &:hover {
            box-shadow: ${isActive
            ? '0 12px 35px rgba(255, 193, 7, 0.4), 0 0 20px rgba(255, 193, 7, 0.2)'
            : '0 4px 12px rgba(255, 193, 7, 0.2)'};
          }
        `;
      default:
        return css`
          border: 1px solid #ddd;
          background: white;

          &:hover {
            box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
          }
        `;
    }
  }}
`;

const DescriptionText = styled.p<{ $isActive?: boolean }>`
  font-size: 12px !important;
  font-style: italic;
  color: ${props => props?.$isActive ? 'black' : '#6b7280'};
  margin-top: 0.5rem;
`;

const ValueText = styled.div`
  font-size: 1.35rem !important;
  font-weight: bold;
  color: #222;
`;

const TitleText = styled.h3<{ $variant: EmissionCardProps['variant'], $isActive?: boolean }>`
  color: ${props => props.$isActive ? 'black' : '#738c73'} ;
  font-weight: bold !important;
  margin: 0 !important;
`;

export const EmissionCard: React.FC<EmissionCardProps> = ({
  title,
  value,
  unit,
  description,
  variant = 'default',
  icon,
  isActive = false,
  onClick,
  additionalDetail
}) => {
  return (
    <VariantWrapper $variant={variant} $isActive={isActive} $onClick={onClick} $hasAdditionalDetail={!!additionalDetail} onClick={onClick}>
      <CardHeader style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', paddingBottom: '0.5rem' }}>
        <TitleText $variant={variant} $isActive={isActive}>
          {title}
        </TitleText>
        {icon && <div>{icon}</div>}
      </CardHeader>

      <CardContent>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.2rem', flexDirection: 'column' }}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: '1.8rem' }}>
            {additionalDetail && <>
              <ValueText style={{ fontSize: '1.1rem' }}>{formatNumber(additionalDetail.value, 2)}</ValueText>
              <Badge $variant={variant} style={{ fontSize: '0.65rem', padding: '0.2rem 0.4rem' }}>
                <div dangerouslySetInnerHTML={{ __html: additionalDetail.unit }} />
              </Badge>
            </>}
          </div>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.5rem' }}>
            <ValueText>{formatNumber(value, 0)}</ValueText>
            <Badge $variant={variant}><div dangerouslySetInnerHTML={{ __html: unit }} /></Badge>
          </div>
        </div>
        {description && <DescriptionText $isActive={isActive}>{description}</DescriptionText>}
      </CardContent>
    </VariantWrapper>
  );
};
